'use client';

import { Icon } from '@/components/ui/Icon/Icon';
import { cdn } from '@/lib/cdn';
import { Link } from '@/lib/i18n/navigation';
import clsx from 'clsx';
import Image from 'next/image';
import type { CSSProperties } from 'react';
import { useEffect, useRef, useState } from 'react';
import styles from './CategoryBannerItem.module.scss';
import type { CategoryBannerItemProps } from './CategoryBannerItem.types';
import { getEffectiveTransition, normalizeBannerData } from './CategoryBannerItem.utils';

export const CategoryBannerItem = ({
  banner,
  index,
  gridTransition,
  aspectRatio,
  testIdPrefix,
}: CategoryBannerItemProps) => {
  const bannerRef = useRef<HTMLAnchorElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  const testId = (suffix: string) =>
    testIdPrefix ? `${testIdPrefix}-banner-${index}-${suffix}` : undefined;

  const {
    backgroundImageUrl,
    backgroundVideoUrl,
    hasVideo,
    hasImage,
    hasMedia,
    backgroundColor,
    productImageUrl,
    productImageAlt,
    textColor,
    hoverEffect,
    transitionType,
    overlayOpacity,
    textAlignment,
    // TODO: handle contentPosition when design supports it
    // contentPosition,
    parallaxOverlayColor,
    href,
    ctaText,
    ctaColor,
  } = normalizeBannerData(banner);

  const shouldAnimate = gridTransition !== 'none' || transitionType !== 'none';

  useEffect(() => {
    if (!shouldAnimate) {
      const rafId = requestAnimationFrame(() => setIsVisible(true));
      return () => cancelAnimationFrame(rafId);
    }

    const staggerDelay = gridTransition === 'stagger' ? index * 150 : 0;
    const initialDelay = 50;
    const hasTriggered = { current: false };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTriggered.current) {
          hasTriggered.current = true;
          setTimeout(() => setIsVisible(true), staggerDelay + initialDelay);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (bannerRef.current) {
      observer.observe(bannerRef.current);
    }

    const fallbackTimeout = setTimeout(
      () => {
        if (!hasTriggered.current) {
          hasTriggered.current = true;
          setIsVisible(true);
        }
      },
      staggerDelay + initialDelay + 100
    );

    return () => {
      observer.disconnect();
      clearTimeout(fallbackTimeout);
    };
  }, [shouldAnimate, gridTransition, index]);

  const cardStyle: CSSProperties = {
    '--text-color': textColor || '#FFFFFF',
    '--overlay-opacity': overlayOpacity,
    '--parallax-overlay-color': parallaxOverlayColor,
    ...(aspectRatio && aspectRatio !== 'auto' && { aspectRatio }),
    ...(backgroundColor && !hasMedia && { backgroundColor }),
  } as CSSProperties;

  const effectiveTransition = getEffectiveTransition(transitionType, gridTransition);

  return (
    <Link
      ref={bannerRef}
      href={href}
      className={clsx(
        styles.banner,
        styles[`align-${textAlignment}`],
        styles[`hover-${hoverEffect}`],
        effectiveTransition !== 'none' && styles[`transition-${effectiveTransition}`],
        isVisible && styles.visible
      )}
      data-testid={testId('section')}
    >
      <div className={styles.card} style={cardStyle}>
        {hasMedia && (
          <div className={styles.backgroundWrapper} data-testid={testId('media')}>
            {hasVideo && backgroundVideoUrl && (
              <video
                className={styles.backgroundVideo}
                autoPlay
                muted
                loop
                playsInline
                poster={
                  backgroundImageUrl
                    ? cdn.transformImageUrl(backgroundImageUrl, {
                        width: 600,
                        quality: 80,
                        format: 'auto',
                      })
                    : undefined
                }
              >
                <source src={cdn.transformVideoUrl(backgroundVideoUrl)} type="video/mp4" />
              </video>
            )}

            {hasImage && !hasVideo && (
              <Image
                src={backgroundImageUrl!}
                alt=""
                fill
                className={styles.backgroundImage}
                sizes="(max-width: 768px) 100vw, 25vw"
              />
            )}

            {hoverEffect === 'parallax' && (
              <div className={styles.parallaxOverlay} aria-hidden="true" />
            )}
          </div>
        )}

        {!hasMedia && backgroundColor && <div className={styles.colorBackground} />}

        <div className={styles.gradient} aria-hidden="true" />

        {productImageUrl && (
          <div className={styles.productImageWrapper}>
            <Image
              src={productImageUrl}
              alt={productImageAlt}
              width={300}
              height={300}
              className={styles.productImage}
            />
          </div>
        )}
      </div>

      <div className={styles.content}>
        {banner.subtitle && (
          <span className={styles.subtitle} data-testid={testId('subtitle')}>
            {banner.subtitle}
          </span>
        )}
        {banner.title && (
          <h3 className={styles.title} data-testid={testId('title')}>
            {banner.title}
          </h3>
        )}
        {ctaText && (
          <span
            className={styles.cta}
            data-testid={testId('cta')}
            style={ctaColor ? ({ '--cta-color': ctaColor } as CSSProperties) : undefined}
          >
            <span className={styles.ctaText}>{ctaText}</span>
            <Icon name="chevron-right" size={20} aria-hidden="true" />
          </span>
        )}
      </div>
    </Link>
  );
};
